import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Space,
  Spin,
  Alert,
  Image,
  InputNumber,
  Divider,
  message,
  Empty
} from 'antd';
import {
  ShoppingCartOutlined,
  HomeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ProductService, Product } from '../services/productService';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';

const { Title, Text, Paragraph } = Typography;

const ProductDetail: React.FC = () => {
  const { supersedeNo, uom } = useParams<{ supersedeNo: string; uom: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, loading: cartLoading } = useCartStore();
  const { user } = useAuthStore();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [imageGallery, setImageGallery] = useState<string[]>([]);

  // The product list hands over its query string when a product is opened, so
  // going back restores the page, search term and category it was left on.
  // Arriving here any other way (a shared link, a reload of a fresh tab) just
  // sends the shopper to the top of the list.
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch || '';
  const productsPath = `/${listSearch}`;

  useEffect(() => {
    if (supersedeNo && uom) {
      loadProductDetail();
    }
  }, [supersedeNo, uom]);

  // Set breadcrumbs when product data is loaded
  useEffect(() => {
    if (product) {
      setBreadcrumbs([
        {
          key: 'home',
          title: 'Home',
          path: productsPath,
          icon: <HomeOutlined />
        },
        {
          key: 'current',
          title: product.Description
        }
      ]);
    }

    // Clear breadcrumbs when component unmounts
    return () => {
      clearBreadcrumbs();
    };
  }, [product, productsPath, setBreadcrumbs, clearBreadcrumbs]);

  const loadProductDetail = async () => {
    if (!supersedeNo || !uom) return;

    setLoading(true);
    setError(null);

    try {
      const priceLevel = ProductService.getCurrentPriceLevel();
      const response = await ProductService.getProductDetail(
        supersedeNo,
        uom,
        priceLevel
      );

      if (response.success && response.data) {
        setProduct(response.data);

        const isPlaceholder = (path: string) => /noimage\.gif$/i.test(path);
        const images: string[] = [];
        const seen = new Set<string>();
        const pushIfReal = (path?: string) => {
          if (!path || isPlaceholder(path)) return;
          // Dedup key: strip leading slashes and decode '%23' so the same file
          // referenced via different paths collapses to one entry.
          const key = path
            .replace(/^\/+/, '')
            .replace(/%23/gi, '#')
            .toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          images.push(path);
        };

        // Multi-image: load all rows from tblInventoryphotoweb in SeqNo order.
        try {
          const gallery = await ProductService.listProductImages(supersedeNo, uom);
          for (const img of gallery) {
            // Use the backend-provided 'PhotoWeb/<file>' path (no leading slash) so
            // getProductImageUrl prepends /api/v1 and normalizes '#' to '_' correctly.
            pushIfReal(img.path || `PhotoWeb/${img.DocName}`);
          }
        } catch (err) {
          console.warn('Failed to load image gallery, falling back to single image:', err);
        }

        // Fallback for legacy products with no rows in tblInventoryphotoweb.
        if (images.length === 0) {
          pushIfReal(response.data.MainPictureFull);
          if (response.data.DocName) {
            pushIfReal(`PhotoWeb/${response.data.DocName}`);
          }
        }

        setImageGallery(images);
        setSelectedImage(images[0] || '');
      } else {
        setError('Product not found');
      }
    } catch (error) {
      console.error('Error loading product detail:', error);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;

    try {
      await addToCart(product.SupersedeNo, product.UOM, quantity);
      message.success(`Added ${quantity} × ${product.Description} to cart`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Failed to add item to cart');
    }
  };

  const handleQuantityChange = (value: number | null) => {
    setQuantity(value || 1);
  };

  const handleBackToProducts = () => {
    navigate(productsPath);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center" style={{ padding: 'var(--spacing-3xl)' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert
          message="Product Not Found"
          description={error || 'The requested product could not be found.'}
          type="error"
          showIcon
          action={
            <Button onClick={handleBackToProducts}>
              Back to Products
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="product-detail-page" style={{ padding: 'var(--spacing-lg)', background: '#f5f5f5' }}>
      <div className="container">
        {/* Back to Products Button */}
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToProducts}
          style={{ marginBottom: 'var(--spacing-lg)' }}
        >
          Back to Products
        </Button>

        <Card style={{ marginBottom: 'var(--spacing-xl)' }}>
          <Row gutter={[32, 32]}>
            {/* Product Images */}
            <Col xs={24} md={12}>
              <div className="product-images">
                {selectedImage ? (
                  <div style={{ marginBottom: 'var(--spacing-md)' }}>
                    <Image
                      width="100%"
                      height={400}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                      src={ProductService.getProductImageUrl(selectedImage)}
                      alt={product.Description}
                      fallback="/images/product-placeholder.svg"
                    />
                  </div>
                ) : (
                  <Empty
                    description="No product image available"
                    style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  />
                )}

                {/* Image Gallery Thumbnails */}
                {imageGallery.length > 1 && (
                  <Row gutter={[8, 8]}>
                    {imageGallery.map((image, index) => (
                      <Col xs={6} sm={6} key={index}>
                        <div
                          className={`thumbnail ${selectedImage === image ? 'selected' : ''}`}
                          style={{
                            border: selectedImage === image ? '2px solid var(--trust-blue)' : '1px solid #d9d9d9',
                            borderRadius: 4,
                            cursor: 'pointer',
                            overflow: 'hidden'
                          }}
                          onClick={() => setSelectedImage(image)}
                        >
                          <img
                            src={ProductService.getProductImageUrl(image)}
                            alt={`${product.Description} ${index + 1}`}
                            style={{
                              width: '100%',
                              height: 60,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/product-placeholder.svg';
                            }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </Col>

            {/* Product Information */}
            <Col xs={24} md={12}>
              <div className="product-info">
                <Title level={2} style={{ color: 'var(--trust-blue)', marginBottom: 'var(--spacing-sm)' }}>
                  {product.Description}
                </Title>

                {/* Product Code & UOM - compact */}
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                  <Text type="secondary">Code: </Text>
                  <Text strong>{product.SupersedeNo}</Text>
                  <Text type="secondary" style={{ marginLeft: 'var(--spacing-md)' }}>Unit: </Text>
                  <Text strong>{product.UOM}</Text>
                </div>

                {/* Pricing - Prominent */}
                <div style={{ marginBottom: 'var(--spacing-lg)', padding: 'var(--spacing-md)', backgroundColor: '#f0f7ff', borderRadius: 8 }}>
                  <Text strong style={{ fontSize: '1.5em', color: 'var(--trust-blue)' }}>
                    {ProductService.priceWithoutGstLabel(product.PriceDesc1)}
                  </Text>
                </div>

                {/* Add to Cart Section - Prominent & Visible */}
                <div className="add-to-cart-section" style={{
                  padding: 'var(--spacing-lg)',
                  backgroundColor: '#fff',
                  border: '2px solid var(--dasoon-primary)',
                  borderRadius: 8,
                  marginBottom: 'var(--spacing-lg)'
                }}>
                  <Space size="middle" wrap style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Text style={{ marginRight: 'var(--spacing-sm)' }}>Qty:</Text>
                      <InputNumber
                        min={1}
                        max={9999}
                        value={quantity}
                        onChange={handleQuantityChange}
                        style={{ width: 80 }}
                        size="large"
                      />
                    </div>

                    <Button
                      type="primary"
                      className="dasoon-btn-primary"
                      size="large"
                      icon={<ShoppingCartOutlined />}
                      onClick={handleAddToCart}
                      loading={cartLoading}
                      style={{
                        minWidth: 180,
                        height: 48,
                        fontSize: '1.1em',
                        fontWeight: 600
                      }}
                    >
                      Add to Cart
                    </Button>
                  </Space>

                  {/* Login prompt for guest users */}
                  {!user && (
                    <div style={{ marginTop: 'var(--spacing-sm)' }}>
                      <Text type="secondary" style={{ fontSize: '0.9em' }}>
                        <Link to="/login">Login</Link> for member pricing or continue as guest.
                      </Text>
                    </div>
                  )}
                </div>

                <Divider />

                {/* Product Details - Below Add to Cart */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {/* Product Details */}
                  {product.prodDetail && (
                    <div>
                      <Text type="secondary">Details: </Text>
                      <Paragraph ellipsis={{ rows: 3, expandable: true }} style={{ marginBottom: 0 }}>
                        {product.prodDetail}
                      </Paragraph>
                    </div>
                  )}
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Additional Product Information */}
        {product.prodDetail && (
          <Card title="Product Description" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <Paragraph>{product.prodDetail}</Paragraph>
          </Card>
        )}

        {/* Technical Specifications Card (if more data available) */}
        <Card title="Product Information" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Product Code:</Text>
                  <Text style={{ marginLeft: 'var(--spacing-sm)' }}>{product.SupersedeNo}</Text>
                </div>
                <div>
                  <Text strong>Unit of Measure:</Text>
                  <Text style={{ marginLeft: 'var(--spacing-sm)' }}>{product.UOM}</Text>
                </div>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Price Level:</Text>
                  <Text style={{ marginLeft: 'var(--spacing-sm)' }}>{ProductService.getCurrentPriceLevel()}</Text>
                </div>
                <div>
                  <Text strong>Net Content:</Text>
                  <Text style={{ marginLeft: 'var(--spacing-sm)' }}>
                    {product.NetContentLabel || product.ConvRate}
                  </Text>
                </div>
                <div>
                  <Text strong>Min Order Qty:</Text>
                  <Text style={{ marginLeft: 'var(--spacing-sm)' }}>{product.DeliveryCalUnit}</Text>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetail;
